import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { apiGet } from '../api/client'
import {
  TICKET_BASE,
  type TicketAffectedItemsPage,
  type TicketChangesPage,
  type TicketRelatedPage,
} from '../api/easeedesk'

/**
 * Numerele de pe filele din stanga ale fisei de tichet.
 *
 * Filele isi cer continutul abia cand se deschid, deci numerele vin dintr-un
 * capat al lor, `GET /tickets/{id}/counts` (`TicketTabCounts` in v4/api), care
 * le numara pe toate o data, cu aceleasi drepturi si aceleasi filtre ca listele.
 * Numai trei file au numar - "History", "Related", "Affected items"; de ce
 * celelalte nu, scrie in capul lui `TicketTabCounts.php`.
 *
 * LA ZERO INSIGNA NU SE DESENEAZA. Asa facea si portalul cu insignele lui
 * (`Modules::relatedTab()` scria ceva numai peste zero), un rand de file cu
 * "0" pe fiecare se citeste ca un rand de alarme, iar randul de file e ingust:
 * fiecare insigna in plus impinge o fila in meniul `≡`. Zero se vede oricum
 * cand se deschide fila - "There are no related tickets".
 */

export interface TicketTabCountsPage {
  ticket: number
  /** Pe `id`-ul filei: `changes-tab`, `history-tab`, `affected-items-tab`. Lipseste = rolul nu vede fila. */
  counts: Record<string, number>
}

export function ticketTabCountsQuery(id: number) {
  return queryOptions({
    /* SUB cheia fisei, dinadins: orice `invalidateQueries(['easeedesk', 'ticket', id])`
       dupa o scriere - comentariu, alocare, status - reincarca si numerele. */
    queryKey: ['easeedesk', 'ticket', id, 'tab-counts'],
    queryFn: () => apiGet<TicketTabCountsPage>(`${TICKET_BASE}/${id}/counts`),
  })
}

/** Ce numara fiecare fila, pentru `aria-label` ("3 related tickets"). */
const NOUNS: Record<string, [string, string]> = {
  'changes-tab': ['history event', 'history events'],
  'history-tab': ['related ticket', 'related tickets'],
  'affected-items-tab': ['affected item', 'affected items'],
}

/**
 * Listele filelor, pe cheia lor de cache, si cum se numara randurile lor.
 *
 * Cand o fila deschisa isi aduce lista, numarul ei se ia CHIAR din lista adusa -
 * nu se mai cere nimic, si insigna nu poate spune altceva decat fila de sub ea.
 * "History" numai cu filtrul "All": cu "Comments" ales, lista e alta decat cea
 * numarata.
 */
const FROM_LIST: Record<string, { tab: string; count(data: unknown, key: readonly unknown[]): number | null }> = {
  changes: {
    tab: 'changes-tab',
    count: (data, key) => (key[4] === 'all' ? ((data as TicketChangesPage).events?.length ?? null) : null),
  },
  related: {
    tab: 'history-tab',
    count: (data) => (data as TicketRelatedPage).groups?.reduce((sum, g) => sum + g.tickets.length, 0) ?? null,
  },
  'affected-items': {
    tab: 'affected-items-tab',
    count: (data) => (data as TicketAffectedItemsPage).items?.length ?? null,
  },
}

/**
 * Listele care, cand se invalideaza dupa o scriere, schimba un numar. Un
 * comentariu nou e si un eveniment nou in "History".
 */
const STALE_WITH = new Set(['changes', 'related', 'affected-items', 'comments'])

/**
 * Numerele filelor, tinute la zi cu ce se scrie pe tichet.
 *
 * Cele mai multe scrieri invalideaza toata fisa, si cheia de mai sus o prinde.
 * Cateva invalideaza numai lista lor (atasarea unui element, pasii unui
 * runbook); pentru ele se asculta cache-ul: o lista invalidata reincarca
 * numerele, iar o lista adusa isi scrie numarul direct.
 */
export function useTicketTabCounts(id: number): Record<string, number> {
  const client = useQueryClient()
  const { data } = useQuery(ticketTabCountsQuery(id))

  useEffect(() => {
    const key = ticketTabCountsQuery(id).queryKey

    return client.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return
      const k = event.query.queryKey
      if (k[0] !== 'easeedesk' || k[1] !== 'ticket' || k[2] !== id || typeof k[3] !== 'string') return

      if (event.action.type === 'success') {
        const list = FROM_LIST[k[3]]
        const n = list?.count(event.action.data, k)
        if (list === undefined || n === null || n === undefined) return
        client.setQueryData(key, (old) =>
          old !== undefined && list.tab in old.counts && old.counts[list.tab] !== n
            ? { ...old, counts: { ...old.counts, [list.tab]: n } }
            : old,
        )
        return
      }

      if (event.action.type === 'invalidate' && STALE_WITH.has(k[3])) {
        void client.invalidateQueries({ queryKey: key, exact: true })
      }
    })
  }, [client, id])

  return data?.counts ?? {}
}

/** Insigna unei file: un numar mic, gri, langa eticheta. Nimic la zero sau fara numar. */
export function TabCount({ tab, count }: { tab: string; count: number | undefined }) {
  if (count === undefined || count <= 0) return null

  const [one, many] = NOUNS[tab] ?? ['item', 'items']
  const label = `${count} ${count === 1 ? one : many}`

  return (
    <span
      className="badge tk-tab-count"
      aria-label={label}
      title={label}
      style={{ padding: '3px 5px', marginLeft: 4, verticalAlign: 'top', fontSize: 10 }}
    >
      {count}
    </span>
  )
}
